#!/usr/bin/env node

import { Command } from 'commander'
import { deploy } from './core/deploy'
import { layer } from './core/layer'
import { release } from './core/release'
import { env } from './utils/env'

const program = new Command()

export interface BasicOptions {
  /**
   * 账户密钥 ID
   * 默认从环境变量 TENCENTCLOUD_SCF_SECRET_ID 中获取
   */
  secretId: string
  /**
   * 账户密钥 Key
   * 默认从环境变量 TENCENTCLOUD_SCF_SECRET_KEY 中获取
   */
  secretKey: string
  /**
   * SCF 所在地域
   * 默认从环境变量 TENCENTCLOUD_SCF_REGION 中获取
   */
  region: string
  /**
   * SCF 所在命名空间
   * 默认从环境变量 TENCENTCLOUD_SCF_NAMESPACE 中获取
   */
  namespace: string
}

export interface DeployOptions extends BasicOptions {
  /**
   * 函数名
   * 默认从环境变量 TENCENTCLOUD_SCF_FUNCTION_NAME 中获取
   */
  functionName: string
}

export interface ReleaseOptions extends DeployOptions {
  layerZip: string
  layerName: string
  replaceLayerName?: string
  layerBucket: string
  layerRegion?: string
  layerPrefix: string
  layerRuntime: string
  layerUseAccelerate: boolean
}

export interface LayerOptions extends BasicOptions {
  /**
   * package.json 路径
   * @default 'package.json'
   */
  packageJson: string

  /**
   * 额外的 npm install 参数
   */
  npmArgs?: string[]
}

/**
 * 净化腾讯云 Description 字段（字符白名单，拒绝 em-dash 等特殊字符）
 */
export { sanitizeDescription } from './utils/description'

program
  .name('tencent-scf-utils')
  .alias('tscf')
  .description('A simple tool for deploying Tencent SCF')
  .version('1.0.0')

addDeployOptions(
  program
  .command('deploy <directory>')
  .description('Deploy a directory to the cloud function platform, and publish a new version')
)
  .action(async (directory, options) => {
    await deploy(directory, options)
  })

addDeployOptions(
  program
    .command('release <directory>')
    .description('Publish a prebuilt Layer, bind it to $LATEST, deploy code, and publish a function version')
    .requiredOption('--layer-zip <layerZip>', 'Path to the validated Layer ZIP')
    .option('--layer-name <layerName>', 'Layer name', env.TENCENTCLOUD_SCF_LAYER_NAME)
    .option(
      '--replace-layer-name <replaceLayerName>',
      'Existing Layer name to replace',
      env.TENCENTCLOUD_SCF_REPLACE_LAYER_NAME
    )
    .option('--layer-bucket <layerBucket>', 'Full COS bucket name', env.TENCENTCLOUD_SCF_LAYER_BUCKET)
    .option('--layer-region <layerRegion>', 'COS bucket region', env.TENCENTCLOUD_SCF_LAYER_BUCKET_REGION)
    .option('--layer-prefix <layerPrefix>', 'COS object key prefix', env.TENCENTCLOUD_SCF_LAYER_PREFIX)
    .option('--layer-runtime <layerRuntime>', 'Compatible SCF runtime', env.TENCENTCLOUD_SCF_LAYER_RUNTIME)
    .option(
      '--layer-use-accelerate',
      'Use COS global acceleration for Layer archive uploads',
      env.TENCENTCLOUD_SCF_LAYER_USE_ACCELERATE
    )
)
  .action(async (directory, options) => {
    await release(directory, options)
  })

program
  .command('layer')
  .description('Build and upload a layer.zip to the cloud function platform')
  .option('-p, --package-json <packageJsonPath>', 'Path to package.json', 'package.json')
  .option('-i, --secret-id <SecretId>', 'Account secret id', env.TENCENTCLOUD_SCF_SECRET_ID)
  .option('-k, --secret-key <SecretKey>', 'Account secret key', env.TENCENTCLOUD_SCF_SECRET_KEY)
  .option('--npm-args <args>', 'Additional arguments to pass to npm install (space-separated)', '')
  .action((options) => {
    // 解析 npm 参数
    const npmArgs = options.npmArgs
      ? options.npmArgs.split(' ').filter((arg: string) => arg.trim())
      : []
    layer({ ...options, npmArgs })
  })

function addDeployOptions(command: Command) {
  return command
    .option('-n, --function-name <functionName>', 'Function name', env.TENCENTCLOUD_SCF_FUNCTION_NAME)
    .option('-r, --region <region>', 'SCF region', env.TENCENTCLOUD_SCF_REGION)
    .option('--namespace <namespace>', 'SCF namespace', env.TENCENTCLOUD_SCF_NAMESPACE)
    .option('-i, --secret-id <SecretId>', 'Account secret id', env.TENCENTCLOUD_SCF_SECRET_ID)
    .option('-k, --secret-key <SecretKey>', 'Account secret key', env.TENCENTCLOUD_SCF_SECRET_KEY)
}

void program.parseAsync(process.argv).catch((error: unknown) => {
  console.error(error)
  process.exitCode = 1
})
